import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { Translation, translationsForAttribute } from '../../Translations/helpers';
import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import EditMailPermissionMutation from './EditMailPermissionMutation';
import MailPermissionForm, { MailPermissionData } from './MailPermissionForm';
import UI from '../../../common/UI';
import mailPermissionSchema from './mailPermissionSchema';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useScroll from '../../../common/useScroll';
import useValidation from '../../../common/useValidation';

interface Event {
  id: string;
}

interface MailPermission {
  id: string;
  title: string;
  label?: string;
  events: Event[];
  translations: Translation[];
  interest_list_event?: {
    id: string;
    title: string;
  } | null;
}

interface EditMailPermissionFormProps {
  mailPermission: MailPermission;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const EditMailPermissionForm = ({ mailPermission, onSuccess, onCancel }: EditMailPermissionFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const project = useProject();
  const { scrollTo } = useScroll();

  const form = useForm<MailPermissionData>({
    values: {
      title: mailPermission.title,
      title_translations: translationsForAttribute(mailPermission.translations, 'title'),
      label: mailPermission.label,
      label_translations: translationsForAttribute(mailPermission.translations, 'label'),
      events: mailPermission.events.map(({ id }) => id),
    },
  });

  const [editMailPermission, { loading, error }] = useMutation(
    EditMailPermissionMutation,
    {
      variables: {
        input: {
          id: mailPermission.id,
          ...form.values,
        },
      },
      onCompleted: () => {
        onSuccess?.();
        notifier.success(t('notifications.mail_permission_saved'));
      },
      onError: () => {
        scrollTo(`EditMailPermissionForm_${mailPermission.id}`);
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: mailPermissionSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form
      onSubmit={() => editMailPermission()}
      id={`EditMailPermissionForm_${mailPermission.id}`}
    >
      <UI.ServerErrors error={error} sc={{ mb: 4 }} />

      <MailPermissionForm
        form={form}
        errors={errors}
        mailPermission={mailPermission}
        project={project}
      />

      <UI.HR />

      <UI.FormButtons
        form={form}
        submitLabel={t('save_mail_permission')}
        onClose={onCancel}
        loading={loading}
        valid={valid}
      />
    </UI.Form>
  );
};

export default EditMailPermissionForm;
